import React from "react";
import { motion } from "framer-motion";

import {
    FaGraduationCap,
    FaBookOpen,
    FaLightbulb,
    FaUserTie,
    FaUsers,
    FaChartLine,
    FaBullseye,
    FaComments,
    FaCompass,
    FaSeedling,
    FaFlag,
    FaArrowRight,
    FaCheckCircle,
    FaBriefcase,
} from "react-icons/fa";

import Footer from "./Footer";

const PrincipalMessagePage = () => {
    return (
        <div className="cayep-learning-page">

            <style>{`

                .cayep-learning-page {
                    width: 100%;
                    min-height: 100vh;
                    background: #f0edea;
                    color: #0a0f12;
                    font-family: "Poppins", "Inter", sans-serif;
                    overflow: hidden;
                }

                /* =====================================================
                   HERO
                ===================================================== */

                .cayep-learning-header {
                    position: relative;
                    width: 100%;
                    min-height: 560px;
                    display: flex;
                    align-items: center;
                    overflow: hidden;
                    background:
                        linear-gradient(
                            125deg,
                            #062a4b 0%,
                            #063a6b 55%,
                            #0f6dab 100%
                        );
                }

                .cayep-learning-header::before {
                    content: "";
                    position: absolute;
                    width: 520px;
                    height: 520px;
                    right: -190px;
                    top: -250px;
                    border-radius: 50%;
                    background: rgba(246, 201, 68, 0.16);
                }

                .cayep-learning-header::after {
                    content: "";
                    position: absolute;
                    width: 420px;
                    height: 420px;
                    left: -190px;
                    bottom: -230px;
                    border-radius: 50%;
                    background: rgba(79, 174, 34, 0.12);
                }

                .cayep-header-grid {
                    position: absolute;
                    inset: 0;
                    opacity: 0.10;
                    background-image:
                        linear-gradient(
                            90deg,
                            rgba(255,255,255,0.18) 1px,
                            transparent 1px
                        ),
                        linear-gradient(
                            rgba(255,255,255,0.18) 1px,
                            transparent 1px
                        );
                    background-size: 72px 72px;
                }

                .cayep-learning-header-content {
                    position: relative;
                    z-index: 5;
                    width: min(1250px, 90%);
                    margin: auto;
                    padding: 110px 0;
                    display: grid;
                    grid-template-columns: 1.15fr 0.85fr;
                    align-items: center;
                    gap: 70px;
                }

                .cayep-header-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 9px;
                    padding: 9px 15px;
                    margin-bottom: 22px;
                    border: 1px solid rgba(246, 201, 68, 0.40);
                    border-radius: 30px;
                    background: rgba(246, 201, 68, 0.10);
                    color: #f6c944;
                    font-size: 11px;
                    font-weight: 800;
                    letter-spacing: 1.6px;
                    text-transform: uppercase;
                    backdrop-filter: blur(10px);
                }

                .cayep-header-badge svg {
                    color: #f6c944;
                }

                .cayep-learning-header-content h1 {
                    max-width: 850px;
                    margin: 0;
                    color: #ffffff;
                    font-size: clamp(44px, 6vw, 78px);
                    line-height: 1.01;
                    font-weight: 900;
                    letter-spacing: -3px;
                }

                .cayep-learning-header-content h1 span {
                    color: #f6c944;
                }

                .cayep-learning-header-content p {
                    max-width: 700px;
                    margin: 26px 0 0;
                    color: rgba(255,255,255,0.76);
                    font-size: 15px;
                    line-height: 1.9;
                }

                .cayep-header-actions {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 12px;
                    margin-top: 30px;
                }

                .cayep-header-primary {
                    display: inline-flex;
                    align-items: center;
                    gap: 10px;
                    padding: 14px 20px;
                    border-radius: 11px;
                    background: #f6c944;
                    color: #062a4b;
                    text-decoration: none;
                    font-size: 12px;
                    font-weight: 850;
                    transition:
                        transform 0.25s ease,
                        background 0.25s ease;
                }

                .cayep-header-primary:hover {
                    transform: translateY(-3px);
                    background: #efa81c;
                }

                .cayep-header-secondary {
                    display: inline-flex;
                    align-items: center;
                    gap: 10px;
                    padding: 14px 20px;
                    border-radius: 11px;
                    background: rgba(255,255,255,0.08);
                    border: 1px solid rgba(255,255,255,0.18);
                    color: #ffffff;
                    text-decoration: none;
                    font-size: 12px;
                    font-weight: 750;
                    transition:
                        transform 0.25s ease,
                        background 0.25s ease;
                }

                .cayep-header-secondary:hover {
                    transform: translateY(-3px);
                    background: rgba(255,255,255,0.14);
                }

                /* =====================================================
                   LEARNING COMPASS
                ===================================================== */

                .cayep-learning-compass {
                    position: relative;
                    min-height: 390px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .cayep-compass-ring {
                    position: absolute;
                    width: 330px;
                    height: 330px;
                    border-radius: 50%;
                    border: 1px solid rgba(255,255,255,0.16);
                }

                .cayep-compass-ring::before {
                    content: "";
                    position: absolute;
                    inset: 25px;
                    border-radius: 50%;
                    border: 1px dashed rgba(246,201,68,0.35);
                }

                .cayep-compass-center {
                    position: relative;
                    z-index: 3;
                    width: 125px;
                    height: 125px;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    border-radius: 50%;
                    background: #f6c944;
                    color: #062a4b;
                    box-shadow:
                        0 20px 50px rgba(0,0,0,0.20);
                    text-align: center;
                }

                .cayep-compass-center svg {
                    font-size: 29px;
                    margin-bottom: 7px;
                }

                .cayep-compass-center strong {
                    font-size: 13px;
                    font-weight: 900;
                    letter-spacing: 0.5px;
                }

                .cayep-compass-center span {
                    margin-top: 2px;
                    font-size: 8px;
                    font-weight: 800;
                    text-transform: uppercase;
                    letter-spacing: 1px;
                }

                .cayep-compass-item {
                    position: absolute;
                    z-index: 4;
                    width: 105px;
                    min-height: 91px;
                    padding: 13px 10px;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    border-radius: 16px;
                    background: rgba(255,255,255,0.09);
                    border: 1px solid rgba(255,255,255,0.15);
                    backdrop-filter: blur(12px);
                    text-align: center;
                }

                .cayep-compass-item svg {
                    margin-bottom: 7px;
                    font-size: 18px;
                }

                .cayep-compass-item strong {
                    color: #ffffff;
                    font-size: 11px;
                    font-weight: 800;
                }

                .cayep-compass-item span {
                    margin-top: 3px;
                    color: rgba(255,255,255,0.62);
                    font-size: 8px;
                    line-height: 1.4;
                }

                .cayep-compass-learn {
                    top: 0;
                    left: 50%;
                    transform: translateX(-50%);
                }

                .cayep-compass-grow {
                    right: 0;
                    top: 50%;
                    transform: translateY(-50%);
                }

                .cayep-compass-lead {
                    bottom: 0;
                    left: 50%;
                    transform: translateX(-50%);
                }

                .cayep-compass-impact {
                    left: 0;
                    top: 50%;
                    transform: translateY(-50%);
                }

                .cayep-compass-learn svg {
                    color: #f6c944;
                }

                .cayep-compass-grow svg {
                    color: #4fae22;
                }

                .cayep-compass-lead svg {
                    color: #0f6dab;
                }

                .cayep-compass-impact svg {
                    color: #efa81c;
                }

                .cayep-header-bottom {
                    position: absolute;
                    z-index: 6;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    height: 7px;
                    display: flex;
                }

                .cayep-header-bottom span:nth-child(1) {
                    flex: 2;
                    background: #f6c944;
                }

                .cayep-header-bottom span:nth-child(2) {
                    flex: 1;
                    background: #4fae22;
                }

                .cayep-header-bottom span:nth-child(3) {
                    flex: 1;
                    background: #0f6dab;
                }

                /* =====================================================
                   MESSAGE INTRO
                ===================================================== */

                .cayep-message-section {
                    position: relative;
                    padding: 110px 5%;
                    background:
                        radial-gradient(
                            circle at 12% 20%,
                            rgba(246,201,68,0.11),
                            transparent 25%
                        ),
                        #f0edea;
                }

                .cayep-message-wrapper {
                    width: min(1250px, 100%);
                    margin: auto;
                    display: grid;
                    grid-template-columns: 0.8fr 1.2fr;
                    gap: 70px;
                    align-items: start;
                }

                .cayep-message-side {
                    position: sticky;
                    top: 100px;
                }

                .cayep-message-kicker {
                    color: #4fae22;
                    font-size: 11px;
                    font-weight: 850;
                    letter-spacing: 1.7px;
                    text-transform: uppercase;
                }

                .cayep-message-side h2 {
                    margin: 12px 0 18px;
                    color: #062a4b;
                    font-size: clamp(34px, 4vw, 55px);
                    line-height: 1.05;
                    font-weight: 900;
                    letter-spacing: -2px;
                }

                .cayep-message-side h2 span {
                    color: #efa81c;
                }

                .cayep-message-side > p {
                    margin: 0;
                    color: #5c6973;
                    font-size: 14px;
                    line-height: 1.9;
                }

                .cayep-message-principle {
                    margin-top: 32px;
                    padding: 20px;
                    border-left: 4px solid #f6c944;
                    border-radius: 0 15px 15px 0;
                    background: rgba(255,255,255,0.75);
                }

                .cayep-message-principle strong {
                    display: block;
                    color: #062a4b;
                    font-size: 13px;
                    font-weight: 850;
                }

                .cayep-message-principle span {
                    display: block;
                    margin-top: 7px;
                    color: #687681;
                    font-size: 11px;
                    line-height: 1.7;
                }

                .cayep-message-card {
                    position: relative;
                    padding: 50px;
                    border-radius: 28px;
                    background: #ffffff;
                    border: 1px solid rgba(6,42,75,0.10);
                    box-shadow:
                        0 25px 70px rgba(6,42,75,0.07);
                }

                .cayep-quote-icon {
                    width: 58px;
                    height: 58px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin-bottom: 25px;
                    border-radius: 16px;
                    background: #fff7d6;
                    color: #efa81c;
                    font-size: 22px;
                }

                .cayep-message-card h3 {
                    margin: 0 0 25px;
                    color: #062a4b;
                    font-size: clamp(27px, 3.5vw, 43px);
                    line-height: 1.12;
                    font-weight: 900;
                    letter-spacing: -1.4px;
                }

                .cayep-message-card h3 span {
                    color: #4fae22;
                }

                .cayep-message-card p {
                    margin: 0 0 20px;
                    color: #5e6c76;
                    font-size: 14px;
                    line-height: 2;
                }

                .cayep-message-card p:first-of-type {
                    color: #263d4d;
                    font-size: 16px;
                }

                .cayep-message-highlight {
                    margin: 30px 0;
                    padding: 22px 24px;
                    border-radius: 16px;
                    background:
                        linear-gradient(
                            135deg,
                            #f8f5df,
                            #eef6e9
                        );
                    border: 1px solid rgba(246,201,68,0.30);
                }

                .cayep-message-highlight strong {
                    display: block;
                    margin-bottom: 7px;
                    color: #062a4b;
                    font-size: 13px;
                    font-weight: 850;
                }

                .cayep-message-highlight span {
                    color: #5c6973;
                    font-size: 12px;
                    line-height: 1.75;
                }

                .cayep-message-signature {
                    margin-top: 32px;
                    padding-top: 25px;
                    border-top: 1px solid #e2e7e9;
                }

                .cayep-message-signature strong {
                    display: block;
                    color: #062a4b;
                    font-size: 19px;
                    font-weight: 850;
                }

                .cayep-message-signature span {
                    display: block;
                    margin-top: 5px;
                    color: #4fae22;
                    font-size: 10px;
                    font-weight: 800;
                    letter-spacing: 1px;
                    text-transform: uppercase;
                }

                /* =====================================================
                   LEARNING BELIEFS
                ===================================================== */

                .cayep-beliefs-section {
                    padding: 100px 5%;
                    background: #ffffff;
                }

                .cayep-section-heading {
                    width: min(780px, 100%);
                    margin: 0 auto 52px;
                    text-align: center;
                }

                .cayep-section-heading > span {
                    color: #4fae22;
                    font-size: 11px;
                    font-weight: 850;
                    letter-spacing: 1.7px;
                    text-transform: uppercase;
                }

                .cayep-section-heading h2 {
                    margin: 10px 0 13px;
                    color: #062a4b;
                    font-size: clamp(31px, 4vw, 47px);
                    line-height: 1.1;
                    font-weight: 900;
                    letter-spacing: -1.5px;
                }

                .cayep-section-heading p {
                    margin: 0;
                    color: #687681;
                    font-size: 14px;
                    line-height: 1.85;
                }

                .cayep-beliefs-grid {
                    width: min(1250px, 100%);
                    margin: auto;
                    display: grid;
                    grid-template-columns: repeat(4, 1fr);
                    gap: 20px;
                }

                .cayep-belief-card {
                    position: relative;
                    padding: 30px 25px;
                    min-height: 235px;
                    border-radius: 20px;
                    background: #f8f7f4;
                    border: 1px solid rgba(6,42,75,0.09);
                    transition:
                        transform 0.3s ease,
                        box-shadow 0.3s ease,
                        border-color 0.3s ease;
                }

                .cayep-belief-card:hover {
                    transform: translateY(-8px);
                    border-color: rgba(246,201,68,0.55);
                    box-shadow:
                        0 20px 45px rgba(6,42,75,0.08);
                }

                .cayep-belief-number {
                    position: absolute;
                    top: 20px;
                    right: 22px;
                    color: rgba(6,42,75,0.10);
                    font-size: 32px;
                    font-weight: 900;
                }

                .cayep-belief-icon {
                    width: 47px;
                    height: 47px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin-bottom: 19px;
                    border-radius: 13px;
                    background: #fff7d6;
                    color: #efa81c;
                    font-size: 18px;
                }

                .cayep-belief-card:nth-child(2) .cayep-belief-icon {
                    background: #edf6e9;
                    color: #4fae22;
                }

                .cayep-belief-card:nth-child(3) .cayep-belief-icon {
                    background: #eaf4fb;
                    color: #0f6dab;
                }

                .cayep-belief-card:nth-child(4) .cayep-belief-icon {
                    background: #f3eee1;
                    color: #c0750d;
                }

                .cayep-belief-card h3 {
                    margin: 0 0 10px;
                    color: #062a4b;
                    font-size: 16px;
                    font-weight: 850;
                }

                .cayep-belief-card p {
                    margin: 0;
                    color: #697780;
                    font-size: 12px;
                    line-height: 1.8;
                }

                /* =====================================================
                   LEARNING JOURNEY
                ===================================================== */

                .cayep-journey-section {
                    padding: 105px 5%;
                    background: #062a4b;
                    position: relative;
                    overflow: hidden;
                }

                .cayep-journey-section::before {
                    content: "";
                    position: absolute;
                    width: 500px;
                    height: 500px;
                    right: -230px;
                    top: -240px;
                    border-radius: 50%;
                    background: rgba(246,201,68,0.09);
                }

                .cayep-journey-heading {
                    position: relative;
                    z-index: 2;
                    width: min(750px, 100%);
                    margin: 0 auto 55px;
                    text-align: center;
                }

                .cayep-journey-heading span {
                    color: #f6c944;
                    font-size: 11px;
                    font-weight: 850;
                    letter-spacing: 1.7px;
                    text-transform: uppercase;
                }

                .cayep-journey-heading h2 {
                    margin: 10px 0;
                    color: #ffffff;
                    font-size: clamp(31px, 4vw, 47px);
                    line-height: 1.1;
                    font-weight: 900;
                    letter-spacing: -1.5px;
                }

                .cayep-journey-heading p {
                    margin: 0;
                    color: rgba(255,255,255,0.65);
                    font-size: 13px;
                    line-height: 1.8;
                }

                .cayep-journey-grid {
                    position: relative;
                    z-index: 2;
                    width: min(1250px, 100%);
                    margin: auto;
                    display: grid;
                    grid-template-columns: repeat(4, 1fr);
                    gap: 18px;
                }

                .cayep-journey-card {
                    position: relative;
                    padding: 29px 24px;
                    min-height: 240px;
                    border-radius: 19px;
                    background: rgba(255,255,255,0.07);
                    border: 1px solid rgba(255,255,255,0.11);
                    transition:
                        transform 0.3s ease,
                        background 0.3s ease;
                }

                .cayep-journey-card:hover {
                    transform: translateY(-8px);
                    background: rgba(255,255,255,0.11);
                }

                .cayep-journey-number {
                    color: rgba(255,255,255,0.25);
                    font-size: 12px;
                    font-weight: 850;
                    letter-spacing: 1px;
                }

                .cayep-journey-icon {
                    width: 48px;
                    height: 48px;
                    margin: 25px 0 18px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 13px;
                    background: rgba(246,201,68,0.14);
                    color: #f6c944;
                    font-size: 18px;
                }

                .cayep-journey-card:nth-child(2) .cayep-journey-icon {
                    background: rgba(79,174,34,0.14);
                    color: #7bd64d;
                }

                .cayep-journey-card:nth-child(3) .cayep-journey-icon {
                    background: rgba(15,109,171,0.18);
                    color: #52a9df;
                }

                .cayep-journey-card:nth-child(4) .cayep-journey-icon {
                    background: rgba(239,168,28,0.15);
                    color: #efa81c;
                }

                .cayep-journey-card h3 {
                    margin: 0 0 9px;
                    color: #ffffff;
                    font-size: 17px;
                    font-weight: 850;
                }

                .cayep-journey-card p {
                    margin: 0;
                    color: rgba(255,255,255,0.60);
                    font-size: 12px;
                    line-height: 1.8;
                }

                /* =====================================================
                   FINAL CTA
                ===================================================== */

                .cayep-learning-cta {
                    width: min(1250px, 90%);
                    margin: 90px auto 100px;
                    padding: 55px;
                    border-radius: 28px;
                    position: relative;
                    overflow: hidden;
                    background:
                        linear-gradient(
                            125deg,
                            #f6c944,
                            #efa81c
                        );
                    box-shadow:
                        0 25px 60px rgba(6,42,75,0.13);
                }

                .cayep-learning-cta::before {
                    content: "";
                    position: absolute;
                    width: 350px;
                    height: 350px;
                    right: -130px;
                    top: -180px;
                    border-radius: 50%;
                    background: rgba(255,255,255,0.15);
                }

                .cayep-learning-cta-content {
                    position: relative;
                    z-index: 2;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 35px;
                }

                .cayep-learning-cta h2 {
                    margin: 0 0 10px;
                    color: #062a4b;
                    font-size: clamp(26px, 4vw, 39px);
                    font-weight: 900;
                    letter-spacing: -1px;
                }

                .cayep-learning-cta p {
                    max-width: 720px;
                    margin: 0;
                    color: rgba(6,42,75,0.72);
                    font-size: 13px;
                    line-height: 1.8;
                }

                .cayep-learning-cta-button {
                    display: inline-flex;
                    align-items: center;
                    gap: 10px;
                    padding: 15px 21px;
                    border-radius: 11px;
                    background: #062a4b;
                    color: #ffffff;
                    text-decoration: none;
                    font-size: 12px;
                    font-weight: 850;
                    white-space: nowrap;
                    transition:
                        transform 0.25s ease,
                        background 0.25s ease;
                }

                .cayep-learning-cta-button:hover {
                    transform: translateY(-3px);
                    background: #063a6b;
                }

                /* =====================================================
                   RESPONSIVE
                ===================================================== */

                @media (max-width: 1050px) {

                    .cayep-learning-header-content {
                        grid-template-columns: 1fr;
                        gap: 30px;
                    }

                    .cayep-learning-compass {
                        min-height: 360px;
                        max-width: 500px;
                        margin: auto;
                        width: 100%;
                    }

                    .cayep-message-wrapper {
                        grid-template-columns: 1fr;
                        gap: 45px;
                    }

                    .cayep-message-side {
                        position: static;
                    }

                    .cayep-beliefs-grid,
                    .cayep-journey-grid {
                        grid-template-columns: repeat(2, 1fr);
                    }
                }

                @media (max-width: 700px) {

                    .cayep-learning-header {
                        min-height: auto;
                    }

                    .cayep-learning-header-content {
                        padding: 80px 0 70px;
                    }

                    .cayep-learning-header-content h1 {
                        font-size: 43px;
                        letter-spacing: -1.8px;
                    }

                    .cayep-learning-header-content p {
                        font-size: 13px;
                        line-height: 1.8;
                    }

                    .cayep-learning-compass {
                        min-height: 330px;
                        transform: scale(0.92);
                    }

                    .cayep-message-section {
                        padding: 75px 5%;
                    }

                    .cayep-message-card {
                        padding: 30px 23px;
                        border-radius: 22px;
                    }

                    .cayep-beliefs-section {
                        padding: 75px 5%;
                    }

                    .cayep-beliefs-grid,
                    .cayep-journey-grid {
                        grid-template-columns: 1fr;
                    }

                    .cayep-journey-section {
                        padding: 75px 5%;
                    }

                    .cayep-learning-cta {
                        margin: 70px 5%;
                        padding: 35px 25px;
                    }

                    .cayep-learning-cta-content {
                        flex-direction: column;
                        align-items: flex-start;
                    }

                    .cayep-learning-cta-button {
                        width: 100%;
                        justify-content: center;
                    }
                }

                @media (max-width: 430px) {

                    .cayep-learning-header-content h1 {
                        font-size: 36px;
                    }

                    .cayep-learning-compass {
                        transform: scale(0.78);
                        margin-top: -20px;
                        margin-bottom: -25px;
                    }

                    .cayep-header-actions {
                        flex-direction: column;
                    }

                    .cayep-header-primary,
                    .cayep-header-secondary {
                        width: 100%;
                        justify-content: center;
                    }

                    .cayep-message-side h2 {
                        font-size: 35px;
                    }

                    .cayep-message-card h3 {
                        font-size: 30px;
                    }
                }

                @media (prefers-reduced-motion: reduce) {

                    .cayep-learning-page *,
                    .cayep-learning-page *::before,
                    .cayep-learning-page *::after {
                        animation: none !important;
                        transition: none !important;
                    }
                }

            `}</style>

            {/* =========================================================
                HERO
            ========================================================= */}

            <section className="cayep-learning-header">

                <div className="cayep-header-grid"></div>

                <div className="cayep-learning-header-content">

                    <div>

                        <motion.div
                            className="cayep-header-badge"
                            initial={{
                                opacity: 0,
                                y: 20
                            }}
                            animate={{
                                opacity: 1,
                                y: 0
                            }}
                            transition={{
                                duration: 0.7
                            }}
                        >
                            <FaGraduationCap />

                            CAYEP LEARNING MESSAGE
                        </motion.div>

                        <motion.h1
                            initial={{
                                opacity: 0,
                                y: 35
                            }}
                            animate={{
                                opacity: 1,
                                y: 0
                            }}
                            transition={{
                                duration: 0.8,
                                delay: 0.1
                            }}
                        >
                            Learning Today.
                            <br />
                            <span>Leading Tomorrow.</span>
                        </motion.h1>

                        <motion.p
                            initial={{
                                opacity: 0,
                                y: 25
                            }}
                            animate={{
                                opacity: 1,
                                y: 0
                            }}
                            transition={{
                                duration: 0.8,
                                delay: 0.2
                            }}
                        >
                            At CAYEP Leadership Training, we believe learning
                            is more than acquiring information. It is a
                            continuous journey of developing character,
                            strengthening capability, discovering purpose,
                            and preparing people to lead with confidence.
                        </motion.p>

                        <motion.div
                            className="cayep-header-actions"
                            initial={{
                                opacity: 0,
                                y: 20
                            }}
                            animate={{
                                opacity: 1,
                                y: 0
                            }}
                            transition={{
                                duration: 0.7,
                                delay: 0.35
                            }}
                        >
                            <a
                                href="#message"
                                className="cayep-header-primary"
                            >
                                Read Our Message
                                <FaArrowRight />
                            </a>

                            <a
                                href="#journey"
                                className="cayep-header-secondary"
                            >
                                Explore The Journey
                                <FaCompass />
                            </a>
                        </motion.div>

                    </div>

                    <motion.div
                        className="cayep-learning-compass"
                        initial={{
                            opacity: 0,
                            scale: 0.8
                        }}
                        animate={{
                            opacity: 1,
                            scale: 1
                        }}
                        transition={{
                            duration: 0.9,
                            delay: 0.25
                        }}
                    >

                        <div className="cayep-compass-ring"></div>

                        <div className="cayep-compass-center">
                            <FaGraduationCap />

                            <strong>
                                CAYEP
                            </strong>

                            <span>
                                Learning
                            </span>
                        </div>

                        <div className="cayep-compass-item cayep-compass-learn">
                            <FaBookOpen />

                            <strong>
                                EQUIP
                            </strong>

                            <span>
                                Build knowledge
                            </span>
                        </div>

                        <div className="cayep-compass-item cayep-compass-grow">
                            <FaSeedling />

                            <strong>
                                EMPOWER
                            </strong>

                            <span>
                                Develop capability
                            </span>
                        </div>

                        <div className="cayep-compass-item cayep-compass-lead">
                            <FaFlag />

                            <strong>
                                LEAD
                            </strong>

                            <span>
                                Put learning into action
                            </span>
                        </div>

                        <div className="cayep-compass-item cayep-compass-impact">
                            <FaBullseye />

                            <strong>
                                EDUCATE
                            </strong>

                            <span>
                                Create meaningful value
                            </span>
                        </div>

                    </motion.div>

                </div>

                <div className="cayep-header-bottom">
                    <span></span>
                    <span></span>
                    <span></span>
                </div>

            </section>

            {/* =========================================================
                LEARNING MESSAGE
            ========================================================= */}

            <section
                id="message"
                className="cayep-message-section"
            >

                <div className="cayep-message-wrapper">

                    <motion.div
                        className="cayep-message-side"
                        initial={{
                            opacity: 0,
                            x: -60
                        }}
                        whileInView={{
                            opacity: 1,
                            x: 0
                        }}
                        transition={{
                            duration: 0.8
                        }}
                        viewport={{
                            once: true
                        }}
                    >

                        <div className="cayep-message-kicker">
                            A Message From CAYEP
                        </div>

                        <h2>
                            Your Growth
                            <br />
                            <span>Begins With Learning.</span>
                        </h2>

                        <p>
                            Every meaningful journey begins with the
                            willingness to learn. CAYEP exists to create a
                            learning environment where knowledge can become
                            confidence, confidence can become leadership,
                            and leadership can become positive impact.
                        </p>

                        <div className="cayep-message-principle">

                            <strong>
                                Our Learning Principle
                            </strong>

                            <span>
                                Learn intentionally. Practice consistently.
                                Lead responsibly. Create meaningful impact.
                            </span>

                        </div>

                    </motion.div>

                    <motion.div
                        className="cayep-message-card"
                        initial={{
                            opacity: 0,
                            x: 60
                        }}
                        whileInView={{
                            opacity: 1,
                            x: 0
                        }}
                        transition={{
                            duration: 0.8
                        }}
                        viewport={{
                            once: true
                        }}
                    >

                        <div className="cayep-quote-icon">
                            <FaBookOpen />
                        </div>

                        <h3>
                            Learning Is The Beginning Of
                            <br />
                            <span>Leadership.</span>
                        </h3>

                        <p>
                            Welcome to CAYEP Leadership Training. We believe
                            that every person has the ability to learn,
                            develop, contribute, and grow into greater
                            responsibility.
                        </p>

                        <p>
                            Leadership does not begin with a title. It begins
                            with a willingness to learn. It begins when a
                            person becomes curious, asks better questions,
                            accepts new ideas, develops useful skills, and
                            becomes intentional about personal growth.
                        </p>

                        <p>
                            Our approach to learning connects knowledge with
                            practical development. We want learners to do
                            more than remember information. We want them to
                            understand what they learn, practice it, and
                            discover how it can be applied in their careers,
                            businesses, communities, and everyday lives.
                        </p>

                        <div className="cayep-message-highlight">

                            <strong>
                                Knowledge becomes powerful when it is applied.
                            </strong>

                            <span>
                                That is why our learning journey connects
                                understanding, practice, confidence,
                                leadership, and meaningful action.
                            </span>

                        </div>

                        <p>
                            Whether you are preparing for your career,
                            developing your leadership abilities, exploring
                            entrepreneurship, strengthening your professional
                            skills, or preparing to manage people and
                            organizations, learning gives you a stronger
                            foundation for the journey ahead.
                        </p>

                        <p>
                            CAYEP encourages learners to remain curious,
                            disciplined, responsible, and open to growth.
                            The world continues to change, and effective
                            leaders must be willing to keep learning,
                            adapting, and improving.
                        </p>

                        <p>
                            We also believe that leadership is not only about
                            personal achievement. It is about using what you
                            learn to serve others, solve problems, create
                            opportunities, strengthen organizations, and
                            contribute positively to society.
                        </p>

                        <p>
                            Our hope is that every learning experience becomes
                            a step toward greater confidence, stronger
                            character, clearer purpose, and meaningful
                            leadership.
                        </p>

                        <p>
                            Keep learning. Keep growing. Keep developing the
                            courage to lead. Your next level begins with what
                            you choose to learn today.
                        </p>

                        <div className="cayep-message-signature">

                            <strong>
                                CAYEP Leadership Training
                            </strong>

                            <span>
                                Learn • Grow • Lead • Impact
                            </span>

                        </div>

                    </motion.div>

                </div>

            </section>

            {/* =========================================================
                LEARNING BELIEFS
            ========================================================= */}

            <section className="cayep-beliefs-section">

                <div className="cayep-section-heading">

                    <span>
                        OUR LEARNING PHILOSOPHY
                    </span>

                    <h2>
                        What We Believe About Learning
                    </h2>

                    <p>
                        The principles that shape the way we think about
                        personal development, professional growth, and
                        leadership preparation.
                    </p>

                </div>

                <div className="cayep-beliefs-grid">

                    <motion.div
                        className="cayep-belief-card"
                        whileHover={{
                            y: -8
                        }}
                    >

                        <span className="cayep-belief-number">
                            01
                        </span>

                        <div className="cayep-belief-icon">
                            <FaBookOpen />
                        </div>

                        <h3>
                            Learning Is Continuous
                        </h3>

                        <p>
                            Growth does not stop after school or formal
                            training. Strong learners continue developing
                            throughout their personal and professional
                            journey.
                        </p>

                    </motion.div>

                    <motion.div
                        className="cayep-belief-card"
                        whileHover={{
                            y: -8
                        }}
                    >

                        <span className="cayep-belief-number">
                            02
                        </span>

                        <div className="cayep-belief-icon">
                            <FaSeedling />
                        </div>

                        <h3>
                            Growth Requires Practice
                        </h3>

                        <p>
                            Knowledge becomes stronger through practice,
                            reflection, feedback, and the willingness to
                            improve.
                        </p>

                    </motion.div>

                    <motion.div
                        className="cayep-belief-card"
                        whileHover={{
                            y: -8
                        }}
                    >

                        <span className="cayep-belief-number">
                            03
                        </span>

                        <div className="cayep-belief-icon">
                            <FaUserTie />
                        </div>

                        <h3>
                            Leadership Can Be Developed
                        </h3>

                        <p>
                            Leadership is strengthened through knowledge,
                            self-awareness, communication, responsibility,
                            and practical experience.
                        </p>

                    </motion.div>

                    <motion.div
                        className="cayep-belief-card"
                        whileHover={{
                            y: -8
                        }}
                    >

                        <span className="cayep-belief-number">
                            04
                        </span>

                        <div className="cayep-belief-icon">
                            <FaBullseye />
                        </div>

                        <h3>
                            Learning Should Create Impact
                        </h3>

                        <p>
                            The purpose of development is not simply to know
                            more, but to use knowledge responsibly to create
                            useful and meaningful results.
                        </p>

                    </motion.div>

                </div>

            </section>

            {/* =========================================================
                LEARNING JOURNEY
            ========================================================= */}

            <section
                id="journey"
                className="cayep-journey-section"
            >

                <div className="cayep-journey-heading">

                    <span>
                        THE CAYEP JOURNEY
                    </span>

                    <h2>
                        From Learning To Leadership
                    </h2>

                    <p>
                        Our learning philosophy can be understood as a
                        continuous journey from gaining knowledge to
                        creating meaningful impact.
                    </p>

                </div>

                <div className="cayep-journey-grid">

                    <motion.div
                        className="cayep-journey-card"
                        whileHover={{
                            y: -8
                        }}
                    >

                        <div className="cayep-journey-number">
                            STEP 01
                        </div>

                        <div className="cayep-journey-icon">
                            <FaBookOpen />
                        </div>

                        <h3>
                            Learn
                        </h3>

                        <p>
                            Discover new knowledge, ideas, concepts, and
                            perspectives that expand your understanding.
                        </p>

                    </motion.div>

                    <motion.div
                        className="cayep-journey-card"
                        whileHover={{
                            y: -8
                        }}
                    >

                        <div className="cayep-journey-number">
                            STEP 02
                        </div>

                        <div className="cayep-journey-icon">
                            <FaSeedling />
                        </div>

                        <h3>
                            Grow
                        </h3>

                        <p>
                            Develop your skills, confidence, character, and
                            ability through practice and reflection.
                        </p>

                    </motion.div>

                    <motion.div
                        className="cayep-journey-card"
                        whileHover={{
                            y: -8
                        }}
                    >

                        <div className="cayep-journey-number">
                            STEP 03
                        </div>

                        <div className="cayep-journey-icon">
                            <FaFlag />
                        </div>

                        <h3>
                            Lead
                        </h3>

                        <p>
                            Apply what you have learned by taking
                            responsibility, making decisions, and serving
                            others.
                        </p>

                    </motion.div>

                    <motion.div
                        className="cayep-journey-card"
                        whileHover={{
                            y: -8
                        }}
                    >

                        <div className="cayep-journey-number">
                            STEP 04
                        </div>

                        <div className="cayep-journey-icon">
                            <FaBullseye />
                        </div>

                        <h3>
                            Impact
                        </h3>

                        <p>
                            Turn your knowledge and leadership into practical
                            contributions that create value around you.
                        </p>

                    </motion.div>

                </div>

            </section>

            {/* =========================================================
                FINAL CTA
            ========================================================= */}

            <section className="cayep-learning-cta">

                <div className="cayep-learning-cta-content">

                    <div>

                        <h2>
                            Your Next Level Begins With Learning.
                        </h2>

                        <p>
                            Explore CAYEP training opportunities and take
                            another step toward developing your leadership,
                            career, business, and professional capabilities.
                        </p>

                    </div>

                    <a
                        href="#courses"
                        className="cayep-learning-cta-button"
                    >
                        Explore Learning
                        <FaArrowRight />
                    </a>

                </div>

            </section>

            <Footer />

        </div>
    );
};

export default PrincipalMessagePage;
